// Customer page section (office and admin, Session AUH, email system Phase 7, item 3b): payment terms and chasing.
//
// PAYMENT TERMS: the customer's own terms in days (many managing agents pay at 30 days by their own terms). Blank means ELE's
// standard terms. It is the due date of every Xero DRAFT the platform raises for them from now on, so the invoice, the aged
// debt list and the debtor chase agree on one date and nobody is chased before it. Drafts already raised keep theirs.
// CHASING: never chase this customer at all, or never chase them firmly (their chases stay soft, and an unpaid non-contract
// debt becomes a phone call instead of a final notice). Saved through a two-step confirm; audited server side.
import { useEffect, useState } from "react";
import { api, type ChaseCustomerHold } from "../../lib/api";
import { formatDateLong } from "../../lib/format";
import { customers, type Customer } from "../../lib/resources";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { buttonClasses } from "../../components/Button";

const FIELD_INPUT = "w-28 rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green";
const FIELD_LABEL = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

// Session BAB: "we've got this". Pauses the daily run's drafting for this customer until a date, with a reason, so a person
// handling them by hand is never chased over. Previewed, two-step confirmed, audited, and clearable. At most 90 days.
function CustomerChaseHold({ customerId }: { customerId: string }) {
  const [hold, setHold] = useState<ChaseCustomerHold | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [until, setUntil] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const h = await api.chases.getHold(customerId);
        if (live) setHold(h);
      } catch {
        // The card simply offers to set a hold; a failed read is never shown as "no hold" in words.
      } finally {
        if (live) setLoaded(true);
      }
    })();
    return () => {
      live = false;
    };
  }, [customerId]);

  async function write(nextUntil: string | null, nextReason: string | null) {
    setBusy(true);
    setError(null);
    try {
      await api.chases.setHold(customerId, nextUntil, nextReason, false);
      setHold(await api.chases.getHold(customerId));
      setUntil("");
      setReason("");
    } catch (e) {
      setError(friendlyApiError(e, "Could not save the hold."));
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return null;
  const canSet = /^\d{4}-\d{2}-\d{2}$/.test(until) && reason.trim().length >= 3;
  return (
    <div className="mt-5 border-t border-ele-hairline pt-4" aria-label="Chase hold">
      <h3 className="mb-1 font-heading text-base text-ele-strong">Hold chasing: we have got this</h3>
      {hold?.active ? (
        <div>
          <p className="text-sm text-ele-strong" data-testid="chase-hold-active">
            On hold until {formatDateLong(hold.until)}: {hold.reason}
            {hold.setBy ? ` (set by ${hold.setBy})` : ""}
          </p>
          <div className="mt-2">
            <ConfirmButton
              onConfirm={() => void write(null, null)}
              label="Clear hold"
              confirmLabel="Press again to clear"
              busy={busy}
              busyLabel="Clearing..."
              className={buttonClasses("secondary", "sm")}
              armedClassName="!text-ele-green font-semibold"
            />
          </div>
        </div>
      ) : (
        <div>
          <p className="mb-2 text-sm text-ele-body">
            Nothing is chased for this customer until the date, while someone deals with them by hand. Up to 90 days.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="block">
              <span className={FIELD_LABEL}>Hold until</span>
              <input type="date" aria-label="Hold chasing until" value={until} onChange={(e) => setUntil(e.target.value)} className={FIELD_INPUT} />
            </label>
            <label className="block sm:flex-1">
              <span className={FIELD_LABEL}>Who has them, and why</span>
              <input aria-label="Hold reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} className={FIELD_INPUT + " w-full"} />
            </label>
          </div>
          {canSet && (
            <div className="mt-3">
              <ConfirmButton
                onConfirm={() => void write(until, reason.trim())}
                label="Set hold"
                confirmLabel="Press again to set the hold"
                busy={busy}
                busyLabel="Saving..."
                className={buttonClasses("secondary", "sm")}
                armedClassName="!text-ele-green font-semibold"
              />
            </div>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export function CustomerChaseSection({ customer, onSaved }: { customer: Customer; onSaved: (c: Customer) => void }) {
  const [terms, setTerms] = useState(customer.paymentTermsDays == null ? "" : String(customer.paymentTermsDays));
  const [never, setNever] = useState(!!customer.doNotChase);
  const [soft, setSoft] = useState(!!customer.doNotChaseHard);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const termsValue = terms.trim() === "" ? null : /^\d{1,3}$/.test(terms.trim()) ? Number(terms.trim()) : NaN;
  const termsValid = termsValue === null || (Number.isInteger(termsValue) && termsValue >= 0 && termsValue <= 120);
  const changed = termsValue !== (customer.paymentTermsDays ?? null) || never !== !!customer.doNotChase || soft !== !!customer.doNotChaseHard;

  async function save() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const updated = await customers.update(customer.id, { paymentTermsDays: termsValue, doNotChase: never, doNotChaseHard: soft });
      onSaved(updated);
      setMessage("Saved.");
    } catch (e) {
      setError(friendlyApiError(e, "Could not save the payment terms."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Payment terms and chasing">
      <h2 className="mb-2 font-heading text-lg text-ele-blue">Payment terms and chasing</h2>
      <p className="mb-3 text-sm text-ele-body">
        Their own payment terms set the due date on every new draft invoice, so they are never chased before it. Leave blank for our
        standard terms.
      </p>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="block">
          <span className={FIELD_LABEL}>Payment terms (days)</span>
          <input aria-label="Payment terms in days" inputMode="numeric" value={terms} onChange={(e) => setTerms(e.target.value)} placeholder="Standard" className={FIELD_INPUT} />
        </label>
        <label className="flex min-h-11 items-center gap-2 text-sm text-ele-strong sm:min-h-0">
          <input type="checkbox" checked={never} onChange={(e) => setNever(e.target.checked)} className="h-4 w-4 accent-ele-green" />
          Never chase this customer
        </label>
        <label className="flex min-h-11 items-center gap-2 text-sm text-ele-strong sm:min-h-0">
          <input type="checkbox" checked={soft} onChange={(e) => setSoft(e.target.checked)} className="h-4 w-4 accent-ele-green" />
          Only ever chase them softly
        </label>
      </div>
      {!termsValid && <p className="mt-2 text-xs text-ele-danger">Payment terms are a whole number of days, 0 to 120.</p>}
      {soft && <p className="mt-2 text-xs text-ele-body">An unpaid debt on a pro forma invoice becomes a phone call for the office instead of a final notice.</p>}
      {changed && termsValid && (
        <div className="mt-3">
          <ConfirmButton
            onConfirm={() => void save()}
            label="Save"
            confirmLabel="Press again to save"
            busy={busy}
            busyLabel="Saving..."
            className={buttonClasses("secondary", "sm")}
            armedClassName="!text-ele-green font-semibold"
          />
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      {message && (
        <p role="status" className="mt-2 text-sm text-ele-strong">
          {message}
        </p>
      )}
      <CustomerChaseHold customerId={customer.id} />
    </section>
  );
}
